import type { ScreenConfig, ScreenResult, ScreenGenerationSettings } from '@core/types/slot-screen-generator.types';
import type { ImageGenerationSettings, ReferenceImage, ImageModelId } from '../../../core/types/image-generation.types';
import { generateImages } from '../image-generation/ImageGeneratorService';
import { useSlotAssetGeneratorStore } from '../../store/slotAssetGeneratorStore';
import { resolveApiKey } from '../../utils/api-keys';
import { shouldUseProxy, proxyGenerateContent } from '../ai-proxy';

// ─── Editable Component Extraction ──────────────────────────

/**
 * A component extracted from the system prompt JSON that can be individually edited.
 */
export interface EditableComponent {
  id: string;
  name: string;
  path: string;          // e.g. "symbols > S01" or "ui_elements > spin_button"
  position?: string;     // raw position text from JSON
  size?: string;         // raw size text from JSON
}

/**
 * Extract component definitions from the system prompt JSON.
 * Walks the JSON tree to find any objects with id/name + position/size info.
 * Returns a flat list of editable components.
 */
export function extractEditableComponents(systemPromptJson: string): EditableComponent[] {
  try {
    const config = JSON.parse(systemPromptJson);
    const components: EditableComponent[] = [];

    // Extract from symbols
    if (config.slot_machine_layout?.symbols?.symbol_list) {
      for (const sym of config.slot_machine_layout.symbols.symbol_list) {
        components.push({
          id: sym.id || sym.name,
          name: sym.name || sym.id,
          path: `symbols > ${sym.id || sym.name}`,
          position: sym.position || undefined,
          size: sym.size_in_cell || undefined,
        });
      }
    }

    // Extract from ui_elements (recursive)
    if (config.ui_elements) {
      for (const [key, val] of Object.entries(config.ui_elements)) {
        if (val && typeof val === 'object') {
          const elem = val as Record<string, unknown>;
          components.push({
            id: key,
            name: (elem.name as string) || key,
            path: `ui_elements > ${key}`,
            position: (elem.position as string) || undefined,
            size: (elem.size as string) || undefined,
          });
          // Also check nested elements array
          if (Array.isArray(elem.elements)) {
            for (const child of elem.elements) {
              if (child && typeof child === 'object' && (child.name || child.id)) {
                components.push({
                  id: child.id || child.name,
                  name: child.name || child.id,
                  path: `ui_elements > ${key} > ${child.name || child.id}`,
                  position: child.position || undefined,
                  size: child.size || undefined,
                });
              }
            }
          }
        }
      }
    }

    // Extract from background elements
    if (config.background?.elements && Array.isArray(config.background.elements)) {
      config.background.elements.forEach((elem: string, i: number) => {
        components.push({
          id: `bg_element_${i}`,
          name: typeof elem === 'string' ? elem : `Background Element ${i + 1}`,
          path: `background > element_${i}`,
        });
      });
    }

    return components;
  } catch {
    return [];
  }
}

/**
 * Calculate a mask rectangle for a component based on its position/size in the JSON.
 * Returns pixel coordinates relative to the image dimensions.
 * If position data is not available, returns a centered default region.
 */
export function calcComponentMask(
  systemPromptJson: string,
  componentId: string,
  imageWidth: number,
  imageHeight: number
): { x: number; y: number; w: number; h: number } {
  const components = extractEditableComponents(systemPromptJson);
  const comp = components.find(c => c.id === componentId);

  if (!comp) {
    // Default: center 30% of image
    const w = Math.round(imageWidth * 0.3);
    const h = Math.round(imageHeight * 0.3);
    return { x: Math.round((imageWidth - w) / 2), y: Math.round((imageHeight - h) / 2), w, h };
  }

  // Try to parse position/size strings
  // Position might be like "top of game area, y: ~30px, centered horizontally"
  // Size might be like "720 x 120px" or "~130px diameter" or "85% of cell area"
  // This is a best-effort parser for common formats
  let x = 0, y = 0, w = imageWidth * 0.2, h = imageHeight * 0.1;

  if (comp.size) {
    const sizeMatch = comp.size.match(/(\d+)\s*[x×]\s*(\d+)/i);
    if (sizeMatch) {
      w = parseInt(sizeMatch[1]);
      h = parseInt(sizeMatch[2]);
    }
    const diamMatch = comp.size.match(/~?(\d+)px\s*diameter/i);
    if (diamMatch) {
      w = h = parseInt(diamMatch[1]);
    }
    const pctMatch = comp.size.match(/(\d+)%/);
    if (pctMatch) {
      const pct = parseInt(pctMatch[1]) / 100;
      w = imageWidth * pct * 0.2; // approximate cell-relative
      h = w; // square
    }
  }

  if (comp.position) {
    const yMatch = comp.position.match(/y:\s*~?(\d+)/i);
    if (yMatch) y = parseInt(yMatch[1]);
    if (comp.position.toLowerCase().includes('center')) {
      x = (imageWidth - w) / 2;
    }
    if (comp.position.toLowerCase().includes('bottom')) {
      y = imageHeight - h - 20;
    }
    if (comp.position.toLowerCase().includes('top')) {
      y = 20;
    }
  }

  return { x: Math.round(x), y: Math.round(y), w: Math.round(w), h: Math.round(h) };
}

/**
 * Edit an image using Gemini's native image editing (Nano Banana 2).
 * Sends: original full image + optional reference crop + text prompt.
 * Returns an edited version of the original image with the requested changes.
 *
 * API format (per Google AI docs):
 *   contents: [{ parts: [inlineData(original), inlineData(reference), text(prompt)] }]
 *   config: { responseModalities: ['TEXT', 'IMAGE'], imageConfig: { aspectRatio, imageSize } }
 *
 * @param originalImageUrl - The full screen image to edit
 * @param _mask - Unused (kept for API compatibility)
 * @param componentPrompt - Text describing what to change
 * @param _model - Unused (always uses gemini-3.1-flash-image-preview)
 * @param signal - AbortSignal for cancellation
 * @param referenceImageUrl - Optional reference image showing the specific part to change
 */
export async function inpaintRegion(
  originalImageUrl: string,
  _mask: { x: number; y: number; w: number; h: number },
  componentPrompt: string,
  _model: string,
  signal?: AbortSignal,
  referenceImageUrl?: string,
): Promise<string> {
  async function urlToBase64(url: string): Promise<{ data: string; mimeType: string }> {
    const res = await fetch(url);
    const blob = await res.blob();
    const buffer = await blob.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (const b of bytes) binary += String.fromCharCode(b);
    return { data: btoa(binary), mimeType: blob.type || 'image/png' };
  }

  // Build contents: text prompt first, then original image, then reference image
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const parts: any[] = [];

  // 1. Text prompt first (tells the model what to do)
  const editPrompt = referenceImageUrl
    ? `Edit the first image (the full slot game screen). The second image shows the specific part I want to change. ${componentPrompt}. Produce the full edited image preserving everything else exactly the same.`
    : `Edit this slot game screen image. ${componentPrompt}. Produce the full edited image preserving everything else exactly the same.`;
  parts.push({ text: editPrompt });

  // 2. Original full image (the image to edit)
  const originalB64 = await urlToBase64(originalImageUrl);
  parts.push({ inlineData: { data: originalB64.data, mimeType: originalB64.mimeType } });

  // 3. Reference image (the cropped/screenshot part showing what to change)
  if (referenceImageUrl) {
    const refB64 = await urlToBase64(referenceImageUrl);
    parts.push({ inlineData: { data: refB64.data, mimeType: refB64.mimeType } });
  }

  if (signal?.aborted) throw new Error('Aborted');

  const inpaintModel = 'gemini-3.1-flash-image-preview';
  const inpaintConfig = {
    responseModalities: ['TEXT', 'IMAGE'],
    imageConfig: {
      aspectRatio: '9:16',
      imageSize: '1K',
    },
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let response: any;

  if (shouldUseProxy()) {
    // Secure path: route through backend proxy
    response = await proxyGenerateContent(
      {
        model: inpaintModel,
        contents: parts,
        config: inpaintConfig,
      },
      signal,
    );
  } else {
    // Fallback: direct call with user's own API key
    const { GoogleGenAI } = await import('@google/genai');
    const apiKeys = useSlotAssetGeneratorStore.getState().apiKeys;
    const apiKey = resolveApiKey('google', apiKeys.google);
    if (!apiKey) {
      throw new Error('Google API key required for image editing.');
    }

    const ai = new GoogleGenAI({ apiKey });
    response = await ai.models.generateContent({
      model: inpaintModel,
      contents: parts,
      config: inpaintConfig,
    });
  }

  // Extract generated image from response — identical for both paths
  const candidate = response.candidates?.[0];
  if (!candidate?.content?.parts) {
    throw new Error('No response from image editing model');
  }

  for (const part of candidate.content.parts) {
    if (part.inlineData?.data && part.inlineData.mimeType?.startsWith('image/')) {
      // Skip thought images
      if ((part as { thought?: boolean }).thought) continue;
      const bytes = Uint8Array.from(atob(part.inlineData.data), c => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: part.inlineData.mimeType });
      return URL.createObjectURL(blob);
    }
  }

  throw new Error('No edited image returned from model');
}

const generateId = () => Math.random().toString(36).substring(2, 11);

/**
 * Parse the system prompt JSON and build a natural-language context string.
 * Falls back to raw text if JSON is invalid.
 */
export function buildSystemContext(systemPromptJson: string): string {
  try {
    const config = JSON.parse(systemPromptJson);
    const parts: string[] = [];
    parts.push('You are generating a slot game screen image.');

    if (config.meta?.recommended_output_resolution)
      parts.push(`Output resolution: ${config.meta.recommended_output_resolution}`);
    if (config.meta?.aspect_ratio)
      parts.push(`Aspect ratio: ${config.meta.aspect_ratio}`);
    if (config.meta?.format)
      parts.push(`Format: ${config.meta.format}`);
    if (config.global_style?.genre)
      parts.push(`Genre/Theme: ${config.global_style.genre}`);
    if (config.global_style?.camera) {
      const cam = config.global_style.camera;
      const camParts = [cam.angle, cam.lens].filter(Boolean).join(', ');
      if (camParts) parts.push(`Camera: ${camParts}`);
    }
    if (config.global_style?.lighting) {
      const lit = config.global_style.lighting;
      if (lit.key_light) parts.push(`Lighting: ${lit.key_light}`);
      if (lit.shadows) parts.push(`Shadows: ${lit.shadows}`);
    }
    if (config.art_direction)
      parts.push(`Art direction: ${config.art_direction}`);
    if (config.color_palette) {
      const cp = config.color_palette;
      const colors = [
        cp.dominant?.length ? `dominant: ${cp.dominant.join(', ')}` : '',
        cp.accent_warm?.length ? `warm accents: ${cp.accent_warm.join(', ')}` : '',
        cp.accent_cool?.length ? `cool accents: ${cp.accent_cool.join(', ')}` : '',
        cp.highlight?.length ? `highlights: ${cp.highlight.join(', ')}` : '',
      ].filter(Boolean).join('; ');
      if (colors) parts.push(`Color palette: ${colors}`);
      if (cp.overall_saturation) parts.push(`Saturation: ${cp.overall_saturation}`);
    }
    if (config.background) {
      const bg = config.background;
      if (bg.description) parts.push(`Background: ${bg.description}`);
      if (bg.elements?.length) parts.push(`Background elements: ${bg.elements.join(', ')}`);
      if (bg.depth_of_field) parts.push(`Depth of field: ${bg.depth_of_field}`);
    }

    return parts.join('\n');
  } catch {
    return systemPromptJson; // fallback: raw text
  }
}

export interface StructuredScreenPrompt {
  systemContext: string;   // For Gemini systemInstruction
  userPrompt: string;      // Screen description + user prompt only
  fullPrompt: string;      // Combined (for metadata/non-Gemini fallback)
}

/**
 * Build structured prompt for a specific screen.
 * Returns separate system context and user prompt for Gemini systemInstruction support.
 */
export function buildScreenPrompt(systemPromptJson: string, screen: ScreenConfig): StructuredScreenPrompt {
  const systemContext = systemPromptJson;
  const screenDescription = screen.description ? `Screen type: ${screen.name} - ${screen.description}\n\n` : '';
  const userPrompt = `${screenDescription}Generate: ${screen.name} - ${screen.userPrompt}`;
  const parsedContext = buildSystemContext(systemPromptJson);
  const fullPrompt = `${parsedContext}\n${userPrompt}`;
  return { systemContext, userPrompt, fullPrompt };
}

// ─── Dimension Parsing ──────────────────────────────────────

/**
 * Parse width and height from the system prompt JSON meta.recommended_output_resolution.
 * Falls back to 720x1280 if parsing fails.
 */
function parseResolution(systemPromptJson: string): { width: number; height: number } {
  try {
    const meta = JSON.parse(systemPromptJson).meta;
    if (meta?.recommended_output_resolution) {
      const parts = meta.recommended_output_resolution.split('x').map(Number);
      if (parts.length === 2 && parts[0] > 0 && parts[1] > 0) {
        return { width: parts[0], height: parts[1] };
      }
    }
  } catch { /* use defaults */ }
  return { width: 720, height: 1280 };
}

/**
 * Derive aspect ratio string from width/height for the image generation API.
 */
function deriveAspectRatio(width: number, height: number): '9:16' | '16:9' | '3:4' | '4:3' | '1:1' | '3:2' | '2:3' {
  const ratio = width / height;
  if (Math.abs(ratio - 9 / 16) < 0.05) return '9:16';
  if (Math.abs(ratio - 16 / 9) < 0.05) return '16:9';
  if (Math.abs(ratio - 3 / 4) < 0.05) return '3:4';
  if (Math.abs(ratio - 4 / 3) < 0.05) return '4:3';
  if (Math.abs(ratio - 3 / 2) < 0.05) return '3:2';
  if (Math.abs(ratio - 2 / 3) < 0.05) return '2:3';
  if (Math.abs(ratio - 1) < 0.1) return '1:1';
  return height > width ? '9:16' : '16:9';
}

// ─── Delay Helper ──────────────────────────────────────────

function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(timer);
      reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
    }, { once: true });
  });
}

const STAGGER_MS = 1500;

// ─── Screen Image Generation ───────────────────────────────

/**
 * Build ImageGenerationSettings for a screen generation request.
 */
function buildImageSettings(
  structured: StructuredScreenPrompt,
  screen: ScreenConfig,
  settings: ScreenGenerationSettings,
  width: number,
  height: number,
): ImageGenerationSettings {
  const referenceImages: ReferenceImage[] = screen.referenceImages.map((url, i) => ({
    id: `ref-${i}`,
    url,
    fileName: `reference-${i + 1}.png`,
    source: 'upload' as const,
  }));

  return {
    prompt: screen.userPrompt,
    effectivePrompt: structured.userPrompt,
    systemInstruction: structured.systemContext,
    model: settings.model as ImageModelId,
    width,
    height,
    style: 'realistic',
    qualityPreset: '1K',
    batchCount: 1,
    aspectRatio: deriveAspectRatio(width, height),
    seedMode: 'auto',
    styleStrength: 0.5,
    referenceImages,
    removeBackground: false,
  };
}

/**
 * Generate screen images with streaming results via AsyncGenerator.
 * Yields each result as it arrives, enabling progressive UI updates.
 */
export async function* generateScreenImages(
  systemPromptJson: string,
  screen: ScreenConfig,
  settings: ScreenGenerationSettings,
  signal?: AbortSignal,
): AsyncGenerator<{ index: number; result: ScreenResult }> {
  const structured = buildScreenPrompt(systemPromptJson, screen);
  const { width, height } = parseResolution(systemPromptJson);

  for (let i = 0; i < settings.imageCount; i++) {
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    // Stagger requests to avoid rate limits
    if (i > 0) {
      await delay(STAGGER_MS, signal);
    }

    const id = generateId();
    const timestamp = Date.now();

    try {
      const imageSettings = buildImageSettings(structured, screen, settings, width, height);
      const apiKeys = useSlotAssetGeneratorStore.getState().apiKeys;

      const results = await generateImages({
        settings: { ...imageSettings, batchCount: 1 },
        apiKeys,
        signal,
      });

      const genResult = results[0];
      if (!genResult || genResult.status === 'failed') {
        yield {
          index: i,
          result: {
            id,
            imageUrl: '',
            savedPath: '',
            status: 'error',
            error: genResult?.error ?? 'Generation failed',
            timestamp,
            prompt: screen.userPrompt,
            model: settings.model,
            width,
            height,
          },
        };
        continue;
      }

      yield {
        index: i,
        result: {
          id,
          imageUrl: genResult.url,
          savedPath: '',
          status: 'ready',
          timestamp,
          prompt: screen.userPrompt,
          model: settings.model,
          width,
          height,
        },
      };
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') throw err;
      yield {
        index: i,
        result: {
          id,
          imageUrl: '',
          savedPath: '',
          status: 'error',
          error: err instanceof Error ? err.message : String(err),
          timestamp,
          prompt: screen.userPrompt,
          model: settings.model,
          width,
          height,
        },
      };
    }
  }
}

// ─── Idea Chat Image Generation ────────────────────────────

/**
 * Generate images for the Idea Chat feature.
 * Simpler than screen generation — just text + reference images → images.
 * Yields each result as it arrives.
 */
export async function* generateIdea(
  text: string,
  referenceImages: string[],
  settings: { model: string; imageCount: number },
  signal?: AbortSignal,
): AsyncGenerator<{ index: number; imageUrl: string }> {
  const refs: ReferenceImage[] = referenceImages.map((url, i) => ({
    id: `idea-ref-${i}`,
    url,
    fileName: `reference-${i + 1}.png`,
    source: 'upload' as const,
  }));

  const imageSettings: ImageGenerationSettings = {
    prompt: text,
    effectivePrompt: text,
    model: settings.model as ImageModelId,
    width: 720,
    height: 1280,
    style: 'realistic',
    qualityPreset: '1K',
    batchCount: 1,
    aspectRatio: '9:16',
    seedMode: 'auto',
    styleStrength: 0.5,
    referenceImages: refs,
    removeBackground: false,
  };

  for (let i = 0; i < settings.imageCount; i++) {
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    if (i > 0) {
      await delay(STAGGER_MS, signal);
    }

    try {
      const apiKeys = useSlotAssetGeneratorStore.getState().apiKeys;
      const results = await generateImages({
        settings: { ...imageSettings, batchCount: 1 },
        apiKeys,
        signal,
      });

      const genResult = results[0];
      if (genResult && genResult.status !== 'failed') {
        yield { index: i, imageUrl: genResult.url };
      } else {
        yield { index: i, imageUrl: '' };
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') throw err;
      yield { index: i, imageUrl: '' };
    }
  }
}
